import type { FilterField } from '@ooh/ui/components/filter-panel';
import type { FilterOptionsResponse } from '@ooh/contracts';
import { RANGE_FIELD_DEFS } from '../../../lib/range-field-defs';
import type { FilterFieldLabels } from './filter-fields';

/**
 * Catalog-only field builders (material/multiformat/tags/address, the 6
 * numeric ranges, and the catalog-column-filter-parity column-parity/supplier
 * fields) — split out of `filter-fields.ts` to stay within the file's
 * max-lines budget. See `BuildFilterFieldsOptions.includeCatalogOnlyFields`
 * (filter-fields.ts) for why these are `/media-units`-only (never rendered on
 * `/map`).
 */

/**
 * Subset of `CATALOG_ONLY_ARRAY_FIELD_IDS` (filter-fields.ts) whose FilterPanel
 * `select` control is really an array-valued dimension forwarded as-is (not
 * unwrapped to a scalar) — `panelValueToFilters` forwards these directly;
 * every other member of `CATALOG_ONLY_ARRAY_FIELD_IDS` is a `text` field
 * unwrapped to a single scalar value (see `CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS`).
 */
export const CATALOG_ONLY_REAL_ARRAY_FIELD_IDS = ['material', 'multiformat', 'tags'] as const;

/**
 * Catalog-only `text` field ids whose single value rides the FilterPanel array
 * model as a one-element array, then gets unwrapped to a scalar string filter
 * param in `panelValueToFilters` — same treatment `address` already received.
 */
export const CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS = ['address', 'notes', 'roadNumber'] as const;

/**
 * Builds the 6 numeric `range` fields, omitting cost-gated ones when
 * canViewCosts is false, and additionally omitting the rent range field
 * when canViewRent is false (rent's own wildcard cost.view_rent gate, on
 * top of canViewCosts — field-visibility, CU-86cbgnt3z).
 */
function buildRangeFields(
  labels: FilterFieldLabels,
  canViewCosts: boolean,
  canViewRent: boolean,
): FilterField[] {
  return RANGE_FIELD_DEFS.filter(
    (def) => (canViewCosts || !def.costGated) && (canViewRent || !def.rentGated),
  ).map((def) => ({
    type: 'range',
    id: def.id,
    label: labels[def.id],
    unit: def.unit,
  }));
}

/**
 * Builds the column-parity filter fields (notes/roadNumber —
 * catalog-column-filter-parity). `status` is not built here: it was removed
 * entirely (CU-86cb1nq7h follow-up) — redundant with the catalog's detailed
 * availability views. `supplierEmail`/`supplierPhone`/`supplierContactInfo`
 * are not built here either: those filter fields were removed entirely
 * (CU-86cb1nq7h, client request) — that data belongs only in the Suppliers
 * area. `supplierNumber` stays untouched (client-confirmed key identifier,
 * not contact info) and is built separately in `buildSupplierIdentityFields`.
 */
function buildColumnParityFields(labels: FilterFieldLabels): FilterField[] {
  return [
    { type: 'text', id: 'notes', label: labels.notes, placeholder: labels.searchPlaceholder },
    {
      type: 'text',
      id: 'roadNumber',
      label: labels.roadNumber,
      placeholder: labels.searchPlaceholder,
    },
  ];
}

/**
 * Builds the catalog-only fields (material/multiformat/tags/address + numeric
 * ranges + the column-parity fields). See
 * `BuildFilterFieldsOptions.includeCatalogOnlyFields` (filter-fields.ts).
 */
export function buildCatalogOnlyFields(
  opts: FilterOptionsResponse,
  labels: FilterFieldLabels,
  canViewCosts: boolean,
  canViewRent: boolean,
): FilterField[] {
  return [
    {
      type: 'select',
      id: 'material',
      label: labels.material,
      options: (opts.materials ?? []).map((m) => ({ value: m, label: m })),
    },
    {
      type: 'select',
      id: 'multiformat',
      label: labels.multiformat,
      options: (opts.multiformats ?? []).map((m) => ({ value: m, label: m })),
    },
    {
      type: 'select',
      id: 'tags',
      label: labels.tags,
      options: (opts.tagOptions ?? []).map((tag) => ({ value: tag.code, label: tag.label })),
    },
    {
      type: 'text',
      id: 'address',
      label: labels.address,
      placeholder: labels.searchPlaceholder,
    },
    ...buildColumnParityFields(labels),
    ...buildRangeFields(labels, canViewCosts, canViewRent),
  ];
}

/**
 * Builds the `supplierId`/`supplierNumber` fields, omitted (not disabled)
 * when `canViewSupplierDetails` is false. Closes the UI-level supplier filter
 * side channel alongside the server-side strip
 * (`stripSupplierFiltersIfUnauthorized`, catalog-column-filter-parity).
 */
export function buildSupplierIdentityFields(
  opts: FilterOptionsResponse,
  labels: FilterFieldLabels,
  canViewSupplierDetails: boolean,
): { supplierIdField: FilterField[]; supplierNumberField: FilterField[] } {
  if (!canViewSupplierDetails) return { supplierIdField: [], supplierNumberField: [] };
  return {
    supplierIdField: [
      {
        type: 'select',
        id: 'supplierId',
        label: labels.supplierId,
        options: opts.suppliers.map((s) => ({ value: String(s.id), label: s.name })),
      },
    ],
    supplierNumberField: [
      {
        type: 'text',
        id: 'supplierNumber',
        label: labels.supplierNumber,
        placeholder: labels.searchPlaceholder,
      },
    ],
  };
}
